'use client'

import { openLogin, openRegister } from '@/components/auth/open-auth'
import { ModalInfoX2CoolCash } from '@/components/common/coolclub/modal-info-x2-coolcash'
import { Tooltip } from '@/components/ui/tooltip'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { useCoolClubMemberQuery } from '@/hooks/react-query/club/use-club'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { formatNumber } from '@/lib/utils'
import { showPopup } from '@/providers/popup-provider'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import type { ReactNode } from 'react'

interface ProductInfoCoolcashRewardProps {
  regularPrice: number
  // Nội dung hiển thị cùng hàng bên trái (Freeship)
  leading?: ReactNode
  isDoubleDay?: boolean
  checkX2CoolCash?: {
    check_id: string
    check_time: string
    text_time: string
  }
}

type RankingId =
  | '64ea032b9e826a5344bce9a9'
  | '64ea041d9e826a5344bce9b3'
  | '64ea04359e826a5344bce9b4'
  | '64ea04499e826a5344bce9b5'

interface RankingConfig {
  percent: number
  image: string
}

const RANKING_PERCENT: Record<RankingId, RankingConfig> = {
  '64ea032b9e826a5344bce9a9': {
    percent: 0.01,
    image: '/image/November2024/mceclip3_95.png',
  },
  '64ea041d9e826a5344bce9b3': {
    percent: 0.03,
    image: '/image/November2024/mceclip2_43.png',
  },
  '64ea04359e826a5344bce9b4': {
    percent: 0.05,
    image: '/image/November2024/mceclip1_77.png',
  },
  '64ea04499e826a5344bce9b5': {
    percent: 0.07,
    image: '/image/November2024/mceclip0_84.png',
  },
}

export const ProductInfoCoolcashReward = ({
  regularPrice,
  leading,
  isDoubleDay,
  checkX2CoolCash,
}: ProductInfoCoolcashRewardProps) => {
  const { data: userInfo } = useCoolClubMemberQuery()
  const isMobile = useIsMobileClient()
  const defaultRankingId: RankingId = '64ea04499e826a5344bce9b5'
  const userRanking = (userInfo?.ranking?._id as RankingId) || defaultRankingId
  const userLoggedIn = !!userInfo
  const coolcashReward =
    Math.ceil((regularPrice * RANKING_PERCENT[userRanking].percent) / 1000) *
    1000
  const finalCoolcashReward = isDoubleDay ? coolcashReward * 2 : coolcashReward
  const rankingImage = RANKING_PERCENT[userRanking].image
  const t = useTranslations()
  // const isFriday = useMemo(() => {
  //   const today = new Date()
  //   return today.getDay() === 5 // 5 là thứ 5 trong tuần
  // }, [])

  const isFriday = false

  if (finalCoolcashReward <= 0) return leading ?? null

  const openLoginPopup = () => {
    openLogin()
  }

  const openRegisterPopup = () => {
    openRegister()
  }

  const isX2CoolCash =
    (checkX2CoolCash?.check_id && checkX2CoolCash?.check_time) ||
    isDoubleDay ||
    isFriday

  return (
    <div className='flex flex-col gap-2'>
      {isX2CoolCash && (
        <div className='flex flex-wrap items-center gap-1 rounded-lg bg-[#D9DEFF] px-4 py-2 text-sm'>
          {checkX2CoolCash?.check_id && checkX2CoolCash?.check_time && (
            <div className='text-black'>
              {t('product_detail.title.coolcash_reward_info', {
                date: checkX2CoolCash.text_time,
              })}
            </div>
          )}
          {(isDoubleDay || isFriday) && (
            <div className='text-black'>
              {t('product_detail.title.coolcash_reward_today')}.
            </div>
          )}
          <div
            onClick={() => showPopup(<ModalInfoX2CoolCash />)}
            className='cursor-pointer font-semibold text-primary'
          >
            {t('button.learn_more')}
          </div>
        </div>
      )}

      <div className='flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between'>
        {leading}
        <div className='flex items-center gap-4 py-1'>
          <div className='flex items-center gap-2'>
            <Image
              src='/icons/icon-coolcash-box.svg'
              alt='Coolcash'
              width={16}
              height={16}
              unoptimized
              className='h-4 w-4'
            />
            <p className='text-sm leading-5 text-[#020817]'>
              {t.rich('product_detail.title.info_reward.reward', {
                bonus: !userLoggedIn
                  ? t('product_detail.title.info_reward.upto')
                  : '',
                value: formatNumber(finalCoolcashReward),
                amount: (chunks) => <span className='font-bold'>{chunks}</span>,
              })}
            </p>
          </div>
          <Tooltip
            side='bottom'
            trigger={isMobile ? 'click' : 'hover'}
            align='end'
            content={
              <div className='flex max-w-[440px] flex-col gap-2.5 text-xs leading-4 text-neutral-900'>
                <p>
                  {t('product_detail.title.info_reward.description')}{' '}
                  <Image
                    src={ImageHelper.getImageUrl(rankingImage, '', 160)}
                    alt='Coolcash'
                    width={80}
                    height={16}
                    className='inline h-4 w-auto object-contain'
                    unoptimized
                  />
                </p>
                {!userLoggedIn && (
                  <div>
                    <p>
                      {t.rich(
                        'product_detail.title.info_reward.description_2',
                        {
                          strong: (chunks) => (
                            <span className='font-bold'>{chunks}</span>
                          ),
                        }
                      )}
                    </p>
                    <p>
                      <button
                        type='button'
                        onClick={openLoginPopup}
                        className='font-bold text-primary-500 underline'
                      >
                        {t('button.login')}
                      </button>{' '}
                      {t('product_detail.title.or')}{' '}
                      <button
                        type='button'
                        onClick={openRegisterPopup}
                        className='font-bold text-primary-500 underline'
                      >
                        {t('button.register')}
                      </button>
                      {t('product_detail.title.info_reward.register_info')}
                    </p>
                  </div>
                )}
              </div>
            }
          >
            <button
              type='button'
              aria-label={t('product_detail.title.info_reward.description')}
              className='shrink-0'
            >
              <Image
                src='/icons/product/icon-info-outline.svg'
                alt=''
                width={16}
                height={16}
              />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
